import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { env } from '@/utils/constants/env';
import useAuthState from '@/states/auth.state';
import useTenantState from '@/states/tenant.state';

const axiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  config => {
    const token = useAuthState.getState().token;
    const activeTenant = useTenantState.getState().currentTenant;

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (activeTenant) {
      config.headers['X-ACTIVE-TENANT'] = activeTenant.slug;
    }

    config.headers['Content-Type'] = 'application/json';

    return config;
  },
  error => {
    return Promise.reject(error);
  },
);

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve();
    }
  });

  failedQueue = [];
};

axiosInstance.interceptors.response.use(
  response => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 403 Forbidden
    if (error.response?.status === 403) {
      const useErrorState = (await import('@/states/error.state')).default;
      useErrorState.getState().set403Error(true);
      return Promise.reject(error);
    }

    // Handle 401 Unauthorized - coba refresh token dulu
    if (error.response?.status === 401) {
      // Jika sudah pernah di-retry, berarti token memang invalid meskipun belum expired
      // Langsung logout untuk menghindari infinite loop
      if (originalRequest._retry) {
        console.log('[AXIOS INTERCEPTOR] ❌ Retry with refreshed token still failed with 401, forcing logout');
        const { logout } = useAuthState.getState();
        if (logout) await logout({ revoke: false });
        return Promise.reject(error);
      }

      console.log('[AXIOS INTERCEPTOR] 401 Unauthorized detected, attempting token refresh...');

      // Jika sudah ada proses refresh yang berjalan, masukkan ke queue
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return axiosInstance(originalRequest);
          })
          .catch(err => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const rejectedToken = String(originalRequest.headers?.Authorization ?? '').replace(/^Bearer /, '') || undefined;
        const newToken = await useAuthState.getState().getToken(rejectedToken);

        if (newToken) {
          // Refresh token berhasil, retry request dengan token baru
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          processQueue();
          return axiosInstance(originalRequest);
        } else {
          // Refresh token gagal (return null), logout
          processQueue(new Error('Token refresh failed'));
          const { logout } = useAuthState.getState();
          if (logout) await logout({ revoke: false });
          return Promise.reject(error);
        }
      } catch (refreshError) {
        // getToken hanya melempar untuk kegagalan sementara (jaringan/5xx); sesi tetap dipertahankan
        processQueue(refreshError as Error);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export { axiosInstance };
